import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { QueueItems } from '~/shared-components';
import ComposerPopover from '~/composer-popover';
import { getURL } from '~/server/formatters';
import { Notice, Text } from '@bufferapp/ui';

import { RpcUpdate } from '~/post/types';
import { Campaign, CampaignTab } from '~/campaign/types';
import { selectCampaignCounts } from '~/campaign/selectors';
import { formatPostLists } from '~/queue/util';
import {
  useDismissibleBanner,
  DraftsNotInReportsBanner,
} from '~/utils/hooks/useDismissibleBanner';
import { actions as campaignActions } from '../../reducer';

import Header from './Header';
import SkeletonPosts from './SkeletonPosts';
import EmptyStateCampaign from './EmptyState';
import { NavItems } from './NavItems';

/* Styles */
const Container = styled.div`
  margin: 18px;
  max-width: 864px;
`;

const CampaignNotice = styled(Notice)`
  margin-top: 16px;
  width: calc(100% - 52px);
`;

const countMapping = {
  [CampaignTab.Scheduled]: 'scheduled',
  [CampaignTab.Sent]: 'sent',
  [CampaignTab.Drafts]: 'draftsCount',
  [CampaignTab.Approvals]: 'approvalsCount',
};

type ViewCampaignProps = {
  campaign: Campaign;
  campaignPosts: RpcUpdate[];
  hideAnalyzeReport: boolean;
  showCampaignActions: boolean;
  isLoading: boolean;
  hideSkeletonHeader: boolean;
  campaignId: string;
  showComposer: boolean;
  page: CampaignTab;
  hasCampaignsFlip: boolean;
  actions: {
    onCreatePostClick: (campaignId) => void;
    onDeleteCampaignClick: (campaign) => void;
    onEditCampaignClick: (campaignId) => void;
    goToAnalyzeReport: (campaign) => void;
    onComposerCreateSuccess: () => void;
    onConfirmDismissComposer: () => void;
    onCreateCampaignClick: () => void;
  };
  hasAnalyticsOnPosts?: boolean;
  hasTwitterImpressions?: boolean;
  postActions: {
    onEditClick: () => void;
    onDeleteConfirmClick: () => void;
    onSetRemindersClick: () => void;
    onShareNowClick: () => void;
    onRequeueClick: () => void;
    onShareAgainClick: () => void;
    onRescheduleClick: () => void;
    onApproveClick: () => void;
    onRequestApprovalClick: () => void;
    onRevertApprovalClick: () => void;
    onMovePostToDraftsClick: () => void;
  };
};

/* Component */
const ViewCampaign = ({
  campaign,
  campaignPosts,
  isLoading,
  hideSkeletonHeader,
  hideAnalyzeReport,
  showCampaignActions,
  campaignId,
  hasCampaignsFlip,
  showComposer,
  actions,
  postActions,
  hasAnalyticsOnPosts,
  hasTwitterImpressions,
}: ViewCampaignProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const { tab } = useParams() as { tab?: CampaignTab };
  const counts = useSelector(selectCampaignCounts);
  const queueType =
    tab === CampaignTab.Scheduled || tab === CampaignTab.Sent
      ? 'post'
      : 'drafts';

  const items = useMemo(() => {
    const args = {
      posts: campaignPosts,
      orderBy: null,
    } as any;
    return queueType === 'post'
      ? formatPostLists(args)
      : campaignPosts.map((d) => ({ ...d, queueItemType: 'post' }));
  }, [queueType, campaignPosts]);

  const sentView = tab === CampaignTab.Sent;
  if (!hasCampaignsFlip) {
    window.location.href = getURL.getPublishUrl();
    return null;
  }

  // Fetch Data
  // Adding campaignPosts.length to the dependency list will
  // unfortunately trigger a refetch after the initial fetch
  // as length will go from 0 to >0, but we need this check
  // to trigger count updates when edits to campaign items
  // happen
  // TODO: improve refetches by using state updates/pusher events
  useEffect(() => {
    if (tab) {
      const params = {
        campaignId,
        status: tab,
        fullItems: true,
        past: sentView,
      };
      dispatch(campaignActions.fetchCampaign(params));
    }
  }, [campaignId, tab, campaignPosts.length]);

  // Conditions
  const countName = tab ? countMapping[tab] : null;
  const campaignHasPosts = countName ? counts[countName] > 0 : false;
  const onDraftsTab = queueType === 'drafts';
  const { isDismissed, setDismissed } = useDismissibleBanner(
    DraftsNotInReportsBanner
  );
  const showWarningBanner = onDraftsTab && campaignHasPosts && !isDismissed;

  return (
    <Container>
      {/* Header */}
      <Header
        campaignDetails={campaign}
        hideAnalyzeReport={hideAnalyzeReport}
        showCampaignActions={showCampaignActions}
        onCreatePostClick={actions.onCreatePostClick}
        onDeleteCampaignClick={actions.onDeleteCampaignClick}
        onEditCampaignClick={actions.onEditCampaignClick}
        goToAnalyzeReport={actions.goToAnalyzeReport}
        isLoading={isLoading && !hideSkeletonHeader}
      />

      {/* Navigation */}
      <NavItems campaignId={campaignId} />

      {/* Content */}
      {isLoading && <SkeletonPosts />}

      <main id="main">
        {!isLoading && showWarningBanner && (
          <CampaignNotice type="warning" dismiss={() => setDismissed()}>
            <Text>Drafts won&apos;t be shared on reports.</Text>
          </CampaignNotice>
        )}
        {!isLoading && (
          <EmptyStateCampaign hideAnalyzeReport={hideAnalyzeReport} />
        )}
        {!isLoading && campaignHasPosts && (
          <QueueItems
            items={items}
            onDeleteConfirmClick={postActions.onDeleteConfirmClick}
            onSetRemindersClick={postActions.onSetRemindersClick}
            onEditClick={postActions.onEditClick}
            onShareNowClick={postActions.onShareNowClick}
            onRequeueClick={postActions.onRequeueClick}
            onRescheduleClick={postActions.onRescheduleClick}
            onApproveClick={postActions.onApproveClick}
            onRequestApprovalClick={postActions.onRequestApprovalClick}
            onRevertApprovalClick={postActions.onRevertApprovalClick}
            onMovePostToDraftsClick={postActions.onMovePostToDraftsClick}
            type={queueType}
            hasAnalyticsOnPosts={hasAnalyticsOnPosts}
            hasTwitterImpressions={hasTwitterImpressions}
            onShareAgainClick={postActions.onShareAgainClick}
            shouldHideCreatePostButton
            isCampaign
            pinned={false}
            onEmptySlotClick={null}
            onCalendarClick={null}
            onCreatePostClick={null}
            shouldRenderCalendarButtons={false}
            timezoneItems={{}}
          />
        )}
      </main>
      {/* Composer */}
      {showComposer && (
        <ComposerPopover
          onSave={actions.onComposerCreateSuccess}
          type="campaign"
          onConfirmDismissComposer={actions.onConfirmDismissComposer}
        />
      )}
    </Container>
  );
};

ViewCampaign.defaultProps = {
  hasAnalyticsOnPosts: false,
  hasTwitterImpressions: false,
};

export default ViewCampaign;
